import { Icon, Typography, Box } from '@mui/material';
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SeamHealth } from 'app/model';
import { HealthData } from 'app/model/tools/health-data';
import { HealthStatus } from 'app/model/tools/health-status';
import { DataGridFormatters } from 'app/utils/data-grid-formatters';

import { fetchHealthData, selectHealthData, selectSeamHealth } from './slice';

type HealthGridColDef = GridColDef<HealthData>;
type SeamGridColDef = GridColDef<SeamHealth>;

const cols: HealthGridColDef[] = [
    {
        field: 'resource',
        headerName: 'Resource',
        width: 200,
        disableColumnMenu: true,
    },
    {
        field: 'status',
        headerName: '',
        width: 100,
        renderCell: (params) => {
            switch (params.value) {
                case HealthStatus.HEALTHY:
                    return <Icon color='success'>verified</Icon>;
                case HealthStatus.UNKNOWN:
                    return <Icon color='action'>help</Icon>;
                case HealthStatus.INFO:
                    return <Icon color='primary'>info</Icon>;
                case HealthStatus.WARNING:
                    return <Icon color='warning'>warning</Icon>;
                case HealthStatus.ERROR:
                    return <Icon color='error'>new_releases</Icon>;
                default:
                    return 'Unexpected Status';
            }
        },
        disableColumnMenu: true,
    },
    {
        field: 'status_label',
        headerName: 'Status',
        width: 200,
        disableColumnMenu: true,
    },
    {
        field: 'message',
        headerName: 'Message',
        width: 200,
        disableColumnMenu: true,
    },
    {
        field: 'extra',
        headerName: 'Extra Details',
        width: 400,
        flex: 1,
        renderCell: (params: GridRenderCellParams<HealthData, HealthData['extra']>) => {
            if (Array.isArray(params.value)) {
                return params.value.map((data, key) => <span key={key}>{data as string};&nbsp;</span>);
            }

            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if ('object' === typeof params.value && params.value) {
                const data = params.value as Record<string, unknown>;

                return Object.keys(data).map((key) => (
                    <span key={key}>
                        <strong>{key}</strong>: {data[key]};&nbsp;
                    </span>
                ));
            }

            return params.value;
        },
        disableColumnMenu: true,
    },
];

const seamCols: SeamGridColDef[] = [
    {
        field: 'process',
        headerName: 'Process',
        width: 200,
        disableColumnMenu: true,
    },
    {
        field: 'latestFileDate',
        headerName: 'Latest File Date',
        width: 200,
        disableColumnMenu: true,
        valueFormatter: DataGridFormatters.dateFormatter,
    },
    {
        field: 'lastUpdated',
        headerName: 'Latest Updated',
        width: 200,
        disableColumnMenu: true,
        valueFormatter: DataGridFormatters.dateTimeFormatter,
    },
    {
        field: 'isSuccess',
        headerName: 'Is Success',
        width: 100,
        renderCell: (params) => {
            switch (params.value) {
                case true:
                    return <Icon color='success'>verified</Icon>;
                case false:
                    return <Icon color='error'>new_releases</Icon>;
                default:
                    return 'Unexpected Status';
            }
        },
        disableColumnMenu: true,
    },
];

export const HealthReport: React.FunctionComponent = () => {
    const healthData = useSelector(selectHealthData);
    const seamHealth = useSelector(selectSeamHealth);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(fetchHealthData());
    }, [dispatch]);

    return (
        <div>
            <Typography variant='h4' className='mt-2'>
                Seam Update Status
            </Typography>
            <Typography variant='body1' className='mb-2'>
                This table shows statuses reported by SEAM nightly processes. Any failures should be addressed with SEAM
                team.
            </Typography>
            <Box sx={{ width: '100%', display: 'grid', height: 'auto' }}>
                <DataGridPro
                    pagination
                    columns={seamCols}
                    rows={seamHealth ?? []}
                    getRowId={(row) => row.process}
                    autoHeight
                    density='compact'
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'isSuccess', sort: 'asc' }],
                        },
                        pagination: { paginationModel: { pageSize: 6 } },
                    }}
                />
            </Box>
            <Typography variant='h4' className='mt-2'>
                Analog Service Status
            </Typography>
            <Typography variant='body1' className='mb-2'>
                This table shows the latest health status of all analog background processes. Warnings are usually OK.
                Any failure should be addressed with Analog team.
            </Typography>
            <Box sx={{ width: '100%', display: 'grid', height: 'auto' }}>
                <DataGridPro columns={cols} rows={healthData ?? []} getRowId={(row) => row.resource} autoHeight />
            </Box>
        </div>
    );
};
